import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SquareCheckBig } from "lucide-react";
import { useSelector } from "react-redux";
import { createUserNotifications, getUserNotifications, updateUserNotifications, useAppDispatch } from "../../../redux";
import { RoleEnum } from "../../shared/model";
import Card from "../../../components/ui/card";
import { Switch } from "../../../components/ui/switch";

export default function NotificationComponent() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { notificationSettings } = useSelector((state: any) => state.client);
    const { user } = useSelector((state: any) => state.auth);
    const isProfessional =
        Number(user?.role?.id) === RoleEnum.professional ||
        Number(user?.role?.id) === RoleEnum.internationl_professional;
    const [settings, setSettings] = useState({
        notifications: {
            email: {
                projectUpdates: false,
                messages: false,
                marketing: false,
            },
            push: {
                projectUpdates: false,
                messages: false,
                marketing: false,
            },
        },
    });
    useEffect(() => {
        if (notificationSettings) {
            setSettings(prev => ({
                ...prev, // preserve existing privacy settings
                notifications: {
                    email: {
                        projectUpdates: notificationSettings.emailProject || false,
                        messages: notificationSettings.emailMessage || false,
                        marketing: notificationSettings.emailMarketing || false,
                    },
                    push: {
                        projectUpdates: notificationSettings.pushProject || false,
                        messages: notificationSettings.pushMessage || false,
                        marketing: notificationSettings.pushMarketing || false,
                    },
                },
            }));
            if (isProfessional) {
                setSettings(prev => ({
                    ...prev, // preserve existing privacy settings
                    notifications: {
                        email: {
                            ...prev.notifications.email,
                            invite: notificationSettings.emailInvite || false,
                        },
                        push: {
                            ...prev.notifications.push,
                            invite: notificationSettings.pushInvite || false,
                        },
                    },
                }));
            } else {
                setSettings(prev => ({
                    ...prev, // preserve existing privacy settings
                    notifications: {
                        email: {
                            ...prev.notifications.email,
                            proposals: notificationSettings.emailProposal || false,
                        },
                        push: {
                            ...prev.notifications.push,
                            proposals: notificationSettings.pushProposal || false,
                        },
                    },
                }));
            }
        }

        if (isProfessional) {
            setSettings(prev => ({
                ...prev, // preserve existing privacy settings
                notifications: {
                    email: {
                        ...prev.notifications.email,
                        invite: false, // Enable project updates by default for professionals
                    },
                    push: {
                        ...prev.notifications.push,
                        invite: false, // Enable project updates by default for professionals
                    },
                },
            }));
        } else {
            setSettings(prev => ({
                ...prev, // preserve existing privacy settings
                notifications: {
                    email: {
                        ...prev.notifications.email,
                        proposals: false, // Enable project updates by default for professionals
                    },
                    push: {
                        ...prev.notifications.push,
                        proposals: false, // Enable project updates by default for professionals
                    },
                },
            }));
        }
    }, [notificationSettings]);

    const handleInputChange = (section, field, value) => {
        setSettings(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value,
            },
        }));
    };
    useEffect(() => {
        getUserNotificationData();
    }, []);
    const getUserNotificationData = async () => {
        await dispatch(getUserNotifications({ id: user?.id }));
    };

    const getNotificationPayload = () => {
        return {
            userId: {
                id: user?.id,
            },
            pushMarketing: settings.notifications.push.marketing,
            pushProposal: settings.notifications.push["proposals"] || false,
            pushMessage: settings.notifications.push.messages,
            pushProject: settings.notifications.push.projectUpdates,
            pushInvite: settings.notifications.push["invite"] || false,
            emailMarketing: settings.notifications.email.marketing,
            emailProposal: settings.notifications.email["proposals"] || false,
            emailMessage: settings.notifications.email.messages,
            emailProject: settings.notifications.email.projectUpdates,
            emailInvite: settings.notifications.email["invite"] || false,
        };
    };

    const handleSubmit = async () => {
        const payload = getNotificationPayload();

        if (notificationSettings?.id) {
            await dispatch(updateUserNotifications({ id: notificationSettings.id, data: payload }));
        } else {
            await dispatch(createUserNotifications({ data: payload }));
        }
    };
    const hasAtLeastOneNotificationEnabled = () => {
        const { email, push } = settings.notifications;
        return Object.values(email).some(Boolean) || Object.values(push).some(Boolean);
    };

    return (
        <>
            <Card color={undefined} className="p-6">
                <h2 className="text-xl font-semibold mb-6">{t("notificationSettings.title")}</h2>

                {/* Email Notifications */}
                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-medium mb-4">{t("notificationSettings.emailNotifications")}</h3>
                        <div className="space-y-4">
                            {Object.entries(settings.notifications.email).map(([key, value]) => {
                                return (
                                    <div key={key} className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">{t(`notificationSettings.${key}`)}</p>
                                            <p className="text-sm text-gray-600">
                                                {t(`notificationSettings.${key}Desc`)}
                                            </p>
                                        </div>
                                        <Switch
                                            checked={value}
                                            onCheckedChange={checked => {
                                                handleInputChange("notifications", "email", {
                                                    ...settings.notifications.email,
                                                    [key]: checked,
                                                });
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Push Notifications */}
                    <div>
                        <h3 className="text-lg font-medium mb-4">{t("notificationSettings.pushNotifications")}</h3>
                        <div className="space-y-4">
                            {Object.entries(settings.notifications.push).map(([key, value]) => (
                                <div key={key} className="flex items-center justify-between">
                                    <div>
                                        <p className="font-medium">{t(`notificationSettings.${key}`)}</p>
                                        <p className="text-sm text-gray-600">{t(`notificationSettings.push${key.charAt(0).toUpperCase() + key.slice(1)}Desc`)}</p>
                                    </div>
                                    <Switch
                                        checked={value}
                                        onCheckedChange={checked => {
                                            handleInputChange("notifications", "push", {
                                                ...settings.notifications.push,
                                                [key]: checked,
                                            });
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Card>
            <div className="flex justify-end mt-4">
                {notificationSettings == null ? (
                    <button
                        className={`px-4 py-2 text-white rounded-md flex items-center
        ${
            notificationSettings === null && !hasAtLeastOneNotificationEnabled()
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
        }`}
                        onClick={handleSubmit}
                        disabled={notificationSettings === null && !hasAtLeastOneNotificationEnabled()}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("notificationSettings.saveChanges")}</span>
                    </button>
                ) : (
                    <button
                        className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"}
                        onClick={handleSubmit}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("notificationSettings.updateChanges")}</span>
                    </button>
                )}
            </div>
        </>
    );
}
